import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import { image_styles } from '@/constants/image_styles';

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v'];

const isVideoUrl = (url?: string): boolean => {
  if (!url) return false;
  const cleanUrl = url.toLowerCase().split('?')[0].split('#')[0];
  return VIDEO_EXTENSIONS.some((ext) => cleanUrl.endsWith(ext));
};

const ImageStyles = ({ showImages = true }: { showImages?: boolean }) => {
  const { t } = useLocale('ai-image-generator');

  // Early return with optimized layout when images are not shown
  if (!showImages) {
    return (
      <div className='bg-white dark:bg-slate-900'>
        <div className='mx-auto max-w-7xl py-16 px-4 sm:px-6 lg:py-24 lg:px-8'>
          <div className='mb-12 max-w-2xl'>
            <h2 className='mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200'>
              {t('Supported Styles')}
            </h2>
            <p className='mt-4 text-lg text-gray-500 dark:text-slate-400'>
              {t(
                'Our AI image generator supports 20+ styles and all diverse styles - just select the appropriate style and enter text in your language of choice.',
              )}
            </p>
          </div>
          <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'>
            {image_styles
              .filter((style) => !style.isHidden)
              .map((style) => (
                <Link
                  key={style.slug}
                  href={`/ai-image-generator/${style.slug}`}
                  className='group relative flex items-center rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:shadow-md hover:border-indigo-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-indigo-600'
                >
                  <div className='flex-1'>
                    <p className='text-sm font-semibold text-gray-900 group-hover:text-indigo-600 dark:text-slate-200 dark:group-hover:text-indigo-400'>
                      {t(style.name)}
                    </p>
                  </div>
                  <svg
                    className='h-5 w-5 text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z'
                      clipRule='evenodd'
                    />
                  </svg>
                </Link>
              ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white dark:bg-slate-900'>
      <div className='mx-auto max-w-7xl py-16 px-4 sm:px-6 lg:py-24 lg:px-8'>
        <div className=' mb-16 max-w-2xl'>
          <h2 className='mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200'>
            {t('Supported Styles')}
          </h2>
          <p className='mt-4 text-lg text-gray-500 dark:text-slate-400'>
            {t(
              'Our AI image generator supports 20+ styles and all diverse styles - just select the appropriate style and enter text in your language of choice.',
            )}
          </p>
        </div>
        <ul
          role='list'
          className='grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-4 xl:gap-x-8'
        >
          {image_styles
            .filter((style) => !style.isHidden)
            .map((style) => (
              <Link key={style.slug} href={`/ai-image-generator/${style.slug}`}>
                <li className='relative'>
                  <div className='group  block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100'>
                    {isVideoUrl(style.images[0].url) ? (
                      <video
                        src={style.images[0].url}
                        width={300}
                        height={300}
                        className='pointer-events-none h-full w-full object-cover group-hover:opacity-75'
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload='metadata'
                        aria-label={style.images[0].prompt}
                      />
                    ) : (
                      <Image
                        src={style.images[0].url}
                        width={300}
                        height={300}
                        className='pointer-events-none object-cover group-hover:opacity-75'
                        alt={style.images[0].prompt}
                      />
                    )}
                  </div>
                  <p className='pointer-events-none mt-2 block truncate text-sm font-medium text-gray-900 dark:text-slate-300'>
                    {t(style.name)}
                  </p>
                </li>
              </Link>
            ))}
        </ul>
      </div>
    </div>
  );
};

export default ImageStyles;
